import * as React from 'react';
import ReactSwitch from 'react-switch';
import { AdminSettingsSection, Button, Input } from 'src/components';
import { Trash } from 'src/common/icons';
import { useAlerts } from 'src/common/AlertManager';
import {
  useAddMigrationKeyMutation,
  useChallengeMigrationKeyMutation,
  useRemoveMigrationKeyMutation,
  useSetMigrationProviderMutation,
  useVerifyMigrationKeyMutation,
} from 'src/api/mutations/migrationMutations';
import { MigrationChallenge, MigrationKey, MigrationOverview } from 'src/types/TMigration';
import { config } from 'src/utils/config';
import { CopyableValue, Note, Pill, Row, errorText, timeAgo } from './parts';
import styles from './styles.module.scss';

/**
 * "Serving your old URLs" — turning it on, and the DNS change that does it.
 *
 * Three settings and one disclosure. The first version of this screen spent two
 * whole sections on this: one asking the customer to type an identifier we
 * could already read out of their delivery URL, and another wrapped around a
 * single toggle.
 *
 * What genuinely belongs to the customer here is the ownership proof. An
 * identifier decides which account a request is served from, so until ownership
 * is proved the row does not resolve — otherwise anyone could type another
 * company's cloud name and have us pull their images into an account they
 * control. With API credentials saved this happens by itself when the
 * connection is tested; without them, the customer publishes a value we
 * generate at an address only they control.
 */

type Props = {
  overview: MigrationOverview;
};

export const ServingSection: React.FC<Props> = ({ overview }) => {
  const { showSuccess, showAlert } = useAlerts();
  const saveProvider = useSetMigrationProviderMutation();
  const addKey = useAddMigrationKeyMutation();
  const removeKey = useRemoveMigrationKeyMutation();
  const challenge = useChallengeMigrationKeyMutation();
  const verify = useVerifyMigrationKeyMutation();

  const [advanced, setAdvanced] = React.useState(false);
  const [newValue, setNewValue] = React.useState('');
  const [challengeFor, setChallengeFor] = React.useState<MigrationChallenge | null>(null);
  const [verifying, setVerifying] = React.useState('');
  const [outcome, setOutcome] = React.useState<{ ok: boolean; detail: string } | null>(null);

  const cdnHost = (config.cdnUrl || 'cdn.filespin.io').replace(/^https?:\/\//, '').replace(/\/+$/, '');

  const examplePath = overview.provider === 'imagekit'
    ? '/products/shoe.jpg?tr=w-300'
    : '/image/upload/w_300/products/shoe.jpg';

  const keys = overview.keys.filter((k) => k.active);
  const live = keys.filter((k) => k.resolvable);

  /**
   * The identifier that matches the delivery URL currently saved — the one the
   * customer's real links carry.
   *
   * Emphatically NOT "the first key in the list". An account can accumulate
   * others (an old one, one added by hand, one from a previous provider), and
   * the list comes back ordered by type and value rather than by relevance. The
   * first row was being used, so the before/after example and the ownership row
   * could point at an identifier that has nothing to do with the URL the
   * customer is about to test — they paste a link with their real cloud name,
   * get a 404, and the screen is showing them a different name entirely.
   */
  const derived = React.useMemo(() => {
    const base = overview.origin?.base_url || '';
    try {
      const u = new URL(base);
      const first = u.pathname.replace(/^\/+/, '').split('/')[0];
      return (first || u.hostname).toLowerCase();
    } catch {
      return '';
    }
  }, [overview.origin?.base_url]);

  const primary =
    keys.find((k) => k.value === derived) || keys.find((k) => k.resolvable) || keys[0];
  const pullBlockedByService = overview.pull_enabled && !overview.service.pull_enabled;

  const togglePull = async (next: boolean) => {
    if (!overview.provider) return;
    try {
      await saveProvider.mutateAsync({ provider: overview.provider, pull_enabled: next });
      showSuccess(next ? 'Missing images will be fetched from your old provider' : 'Switched off');
    } catch (err) {
      showAlert(errorText(err, 'Could not change that setting'));
    }
  };

  const runVerify = async (k: MigrationKey, method: 'api_credentials' | 'nonce_file') => {
    setVerifying(k.value);
    try {
      const res = await verify.mutateAsync({ key_type: k.key_type, value: k.value, method });
      setOutcome({ ok: !!res.verified, detail: res.detail || (res.verified ? 'Verified' : 'Not verified') });
      if (res.verified) {
        setChallengeFor(null);
        showSuccess(`${k.value} is verified — your old URLs will now be served`);
      }
    } catch (err) {
      showAlert(errorText(err, 'Could not run the check'));
    } finally {
      setVerifying('');
    }
  };

  const startChallenge = async (k: MigrationKey) => {
    setVerifying(k.value);
    // The previous verdict was about a check that no longer applies; leaving it
    // under a freshly issued challenge reads as though the challenge had
    // already failed.
    setOutcome(null);
    try {
      setChallengeFor(await challenge.mutateAsync({ key_type: k.key_type, value: k.value }));
    } catch (err) {
      showAlert(errorText(err, 'Could not start the check'));
    } finally {
      setVerifying('');
    }
  };

  const add = async () => {
    const v = newValue.trim();
    if (!v) {
      showAlert('Enter the hostname to add');
      return;
    }
    try {
      await addKey.mutateAsync({ key_type: 'hostname', value: v });
      setNewValue('');
      showSuccess(`${v} added — verify it to make it live`);
    } catch (err) {
      showAlert(errorText(err, 'Could not add that'));
    }
  };

  const remove = async (k: MigrationKey) => {
    try {
      await removeKey.mutateAsync({ key_type: k.key_type, value: k.value });
      showSuccess(`${k.value} switched off`);
    } catch (err) {
      showAlert(errorText(err, 'Could not switch that off'));
    }
  };

  return (
    <AdminSettingsSection title="Serving your old URLs">
      <p className={styles.sectionLead}>
        Point your images at FileSpin and we answer your existing Cloudinary and ImageKit URLs as they
        are. Nothing in your website&rsquo;s HTML has to change.
      </p>

      <Row
        compact
        label="Ownership"
        help={
          primary
            ? `We check that ${primary.value} really is yours before serving anything for it.`
            : 'Save your provider details above and we will pick this up from your URL.'
        }
      >
        {!primary ? (
          <Pill>Waiting</Pill>
        ) : primary.resolvable ? (
          <span className={styles.inlineStatus}>
            <Pill tone="good">Verified</Pill>
            <span className={styles.inlineMeta}>
              {primary.value}
              {primary.verified_at ? ` · ${timeAgo(primary.verified_at)}` : ''}
            </span>
          </span>
        ) : (
          <span className={styles.inlineStatus}>
            <Pill tone="warn">Not verified</Pill>
            <Button
              mode="tertiary"
              onClick={() =>
                overview.origin?.can_list
                  ? runVerify(primary, 'api_credentials')
                  : startChallenge(primary)
              }
              loading={verifying === primary.value}
            >
              Verify now
            </Button>
          </span>
        )}
      </Row>

      {primary && !primary.resolvable && (
        <Note tone="warn">
          Until this is verified your old URLs will answer <code>404</code>.{' '}
          {overview.origin?.can_list
            ? 'Because your API key is saved, one click is all this takes.'
            : 'Add your API key above and this happens by itself, or publish the file we name below.'}
          {!overview.origin?.can_list && !challengeFor && (
            <div className={styles.sectionActions}>
              <Button
                mode="secondary"
                outlined
                onClick={() => startChallenge(primary)}
                loading={challenge.isLoading}
              >
                Verify by publishing a file
              </Button>
            </div>
          )}
        </Note>
      )}

      {challengeFor && (
        <Note>
          <p className={styles.challengeIntro}>{challengeFor.instructions}</p>
          <Row label="Put this inside the file" stacked>
            <CopyableValue value={challengeFor.nonce} />
          </Row>
          <Row label="We will look here" stacked>
            <CopyableValue value={challengeFor.url} />
          </Row>
          <div className={styles.sectionActions}>
            <Button
              onClick={() => primary && runVerify(primary, 'nonce_file')}
              loading={verify.isLoading}
            >
              I have published it — check now
            </Button>
            <Button mode="flat" onClick={() => setChallengeFor(null)}>
              Cancel
            </Button>
          </div>
        </Note>
      )}

      {outcome && !outcome.ok && <Note tone="warn">{outcome.detail}</Note>}

      <Row
        compact
        label="Fetch missing images from your old provider"
        help="When someone asks for an image we have not copied yet, we fetch it from your old provider, show it, and keep a copy. Leave this on until the move is finished."
      >
        <ReactSwitch
          checked={overview.pull_enabled}
          onChange={togglePull}
          disabled={!overview.provider || saveProvider.isLoading}
          onColor="#2e53e3"
          height={22}
          width={42}
          handleDiameter={16}
          uncheckedIcon={false}
          checkedIcon={false}
        />
      </Row>

      {pullBlockedByService && (
        <Note tone="warn">
          This is on for your account but switched off for this FileSpin deployment, so nothing will be
          fetched. Ask your FileSpin contact to enable it.
        </Note>
      )}

      <Row
        label="Point your images here"
        stacked
        help={
          live.length > 0
            ? 'Change your image hostname to this and keep the rest of every URL exactly as it is.'
            : 'Verify your account above first — this will not serve anything until then.'
        }
      >
        <CopyableValue value={cdnHost} />
      </Row>

      {primary && (
        <>
          <p className={styles.rowHelp}>
            Your URLs change hostname and nothing else. The{' '}
            <code>{primary.value}</code> part is the identifier we read from your delivery URL, and it
            has to match what is in the link you test.
          </p>
        <div className={styles.urlCompare}>
          <div>
            <span className={styles.urlLabel}>before</span>
            <code>
              {(overview.origin?.base_url || (overview.provider === 'imagekit' ? 'ik.imagekit.io/acme' : 'res.cloudinary.com/acme')).replace(/^https?:\/\//, '')}
              {examplePath}
            </code>
          </div>
          <div>
            <span className={styles.urlLabel}>after</span>
            <code>
              {primary.key_type === 'hostname' ? primary.value : `${cdnHost}/${primary.value}`}
              {examplePath}
            </code>
          </div>
        </div>
        </>
      )}

      <button type="button" className={styles.disclosure} onClick={() => setAdvanced((v) => !v)}>
        {advanced ? '▾' : '▸'} Advanced — extra hostnames
      </button>

      {advanced && (
        <div className={styles.advanced}>
          <p className={styles.sectionLead}>
            <strong>Most accounts need nothing here.</strong> The first entry below was read out of your
            delivery URL and is what makes your existing links work — you never had to type it.
          </p>
          <p className={styles.sectionLead}>
            Add a hostname only if your images are also served from a domain of your own, such as{' '}
            <code>images.yourcompany.com</code> instead of your provider&rsquo;s address. In that case
            there is no account id in the URL for us to match on, so we match on the domain instead. They
            all point at the same provider account — a second provider account would need its own set-up.
          </p>

          {keys.length > 0 && (
            <ul className={styles.keyList}>
              {keys.map((k) => (
                <li key={`${k.key_type}:${k.value}`} className={styles.keyRow}>
                  <code>{k.value}</code>
                  <span className={styles.keyMeta}>
                    {k.key_type === 'hostname' ? 'hostname' : 'account id'}
                  </span>
                  {k.resolvable ? <Pill tone="good">Live</Pill> : <Pill tone="warn">Not verified</Pill>}
                  {!k.resolvable && (
                    <Button
                      mode="flat"
                      onClick={() =>
                        overview.origin?.can_list && k.key_type === 'delivery_id'
                          ? runVerify(k, 'api_credentials')
                          : startChallenge(k)
                      }
                      loading={verifying === k.value}
                    >
                      Verify
                    </Button>
                  )}
                  {/* The first entry came from the delivery URL and is what
                      makes every old link work. Switching it off would break the
                      whole migration, so it is shown and not offered. */}
                  {k === primary ? (
                    <span className={styles.keyMeta}>from your URL</span>
                  ) : (
                    <button
                      type="button"
                      className={styles.iconButton}
                      onClick={() => remove(k)}
                      disabled={removeKey.isLoading}
                      aria-label={`Switch off ${k.value}`}
                      title="Switch this off"
                    >
                      <Trash />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/*
            * Hostnames only, deliberately.
            *
            * Every identifier on an account resolves to the SAME
            * `migration_origin` row, and that row holds one delivery base URL
            * with the vendor account id already in it. Two hostnames pointing
            * at one vendor account is exactly right. A second vendor ACCOUNT id
            * is not: both would be fetched from whichever account is configured,
            * so the second one's images would come back wrong or missing. Until
            * migration-onramp can hold an origin per identifier, offering that field would
            * be offering something that cannot work.
            */}
          <div className={styles.addKeyRow}>
            <Input
              value={newValue}
              onChange={setNewValue}
              placeholder="images.example.com"
              onEnter={add}
            />
            <Button mode="secondary" outlined onClick={add} loading={addKey.isLoading}>
              Add hostname
            </Button>
          </div>
        </div>
      )}
    </AdminSettingsSection>
  );
};

export default ServingSection;
